import { db } from '@/lib/firebase';
import { collection, doc, getDoc, setDoc, updateDoc, increment, query, where, getDocs, orderBy } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';

export interface CustomerPoints {
  id: string;
  userId: string;
  totalPoints: number;
  lastUpdated: Timestamp;
  selectedGoalId: string | null;
  selectedGoalPoints: number | null;
  pointsHistory: {
    points: number;
    source: string;
    date: Timestamp;
  }[];
}

const POINTS_PER_DAY = 50;

export const pointsCollection = collection(db, 'customerPoints');

export async function getCustomerPoints(userId: string): Promise<CustomerPoints | null> {
  const pointsDoc = await getDoc(doc(pointsCollection, userId));
  return pointsDoc.exists() ? pointsDoc.data() as CustomerPoints : null;
}

export async function initializeCustomerPoints(userId: string): Promise<void> {
  const pointsDoc = doc(pointsCollection, userId);
  const pointsData: CustomerPoints = {
    id: userId,
    userId,
    totalPoints: 0,
    lastUpdated: Timestamp.now(),
    selectedGoalId: null,
    selectedGoalPoints: null,
    pointsHistory: []
  };
  await setDoc(pointsDoc, pointsData);
}

export async function addPointsForHabits(userId: string): Promise<void> {
  const pointsDoc = doc(pointsCollection, userId);
  const pointsData = await getCustomerPoints(userId);
  
  if (!pointsData) {
    await initializeCustomerPoints(userId);
  }

  // Check if points were already awarded today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const lastPointsAwarded = pointsData?.pointsHistory
    .filter(entry => entry.source === 'Daily Habits')
    .sort((a, b) => b.date.toMillis() - a.date.toMillis())[0];

  if (lastPointsAwarded) {
    const lastPointsDate = lastPointsAwarded.date.toDate();
    lastPointsDate.setHours(0, 0, 0, 0);
    
    if (lastPointsDate.getTime() === today.getTime()) {
      // Points already awarded today
      return;
    }
  }

  // Award points for today
  await updateDoc(pointsDoc, {
    totalPoints: increment(POINTS_PER_DAY),
    lastUpdated: Timestamp.now(),
    pointsHistory: [...(pointsData?.pointsHistory || []), {
      points: POINTS_PER_DAY,
      source: 'Daily Habits',
      date: Timestamp.now()
    }]
  });
}

export async function setSelectedGoal(userId: string, goalId: string | null, pointsRequired: number | null): Promise<void> {
  const pointsDoc = doc(pointsCollection, userId);
  const pointsData = await getCustomerPoints(userId);
  
  if (!pointsData) {
    await initializeCustomerPoints(userId);
  }

  await updateDoc(pointsDoc, {
    selectedGoalId: goalId,
    selectedGoalPoints: pointsRequired,
    lastUpdated: Timestamp.now()
  });
}

export async function getPointsHistory(userId: string, limit: number = 10) {
  const pointsDoc = await getDoc(doc(pointsCollection, userId));
  if (!pointsDoc.exists()) return [];
  
  const pointsData = pointsDoc.data() as CustomerPoints;
  return pointsData.pointsHistory
    .sort((a, b) => b.date.toMillis() - a.date.toMillis())
    .slice(0, limit);
} 